import { apiClient } from '../../utils/apiClient';
import { logger } from '../../utils/logger';
import {
  API_EDI_AFFILIATION_TYPES_LIST,
  API_EDI_AFFILIATION_TYPES_CREATE,
  API_EDI_AFFILIATION_TYPES_GET,
  API_EDI_AFFILIATION_TYPES_UPDATE,
  API_EDI_AFFILIATION_TYPES_DELETE,
} from '../../constants/apiConstants';

// Status codes mirror UserAffiliationType_*_Response.StatusCodes on the BE.
export const STATUS_AFFILIATION_CODE_INVALID_FORMAT = -7400;
export const STATUS_AFFILIATION_CODE_ALREADY_EXISTS = -7401;
export const STATUS_AFFILIATION_BUILT_IN = -7601;
export const STATUS_AFFILIATION_REFERENCED = -7602;

export interface UserAffiliationType {
  id: number;
  partitionKey: string;
  tenantId?: number | null;
  affiliationCode: string;
  displayName: string;
  description?: string | null;
  classification: string;
  sortOrder: number;
  isActive: boolean;
  status: string;
  createdAt?: string | null;
  createdBy?: string | null;
  modifiedAt?: string | null;
  modifiedBy?: string | null;
  dependentUserCount: number;
}

export interface UserAffiliationTypeListParams {
  includeInactive?: boolean;
  classification?: string;
  isActive?: boolean;
}

export interface UserAffiliationTypeListResponse {
  items: UserAffiliationType[];
}

export interface UserAffiliationTypeCreateRequest {
  tenantId?: number | null;
  affiliationCode: string;
  displayName: string;
  description?: string | null;
  sortOrder?: number;
}

export interface UserAffiliationTypeUpdateRequest {
  tenantId?: number | null;
  displayName?: string;
  description?: string | null;
  sortOrder?: number;
  isActive?: boolean;
}

interface ResultEnvelope<T> {
  succeeded: boolean;
  statusCode?: number;
  messages?: string[];
  data?: T;
}

interface CreateInner { affiliationTypeId: number; message: string; }
interface UpdateInner { affiliationTypeId: number; message: string; }
interface DeleteInner { affiliationTypeId: number; dependentUserCount: number; message: string; }
interface GetByIdInner { item: UserAffiliationType | null; message: string; }

/** Thrown when delete fails because users still reference the code (Req 7.8). */
export class AffiliationTypeReferencedError extends Error {
  readonly reasonCode = 'AFFILIATION_TYPE_REFERENCED';
  constructor(message?: string) {
    super(message ?? 'Cannot delete — users still reference this affiliation type.');
    this.name = 'AffiliationTypeReferencedError';
  }
}

/** Thrown when create hits an invalid code format (Req 7.5 / Phase E P5). */
export class AffiliationCodeInvalidError extends Error {
  readonly reasonCode = 'AFFILIATION_CODE_INVALID_FORMAT';
  constructor(message?: string) {
    super(message ?? 'Affiliation code must be lowercase, snake_case, starting with a letter.');
    this.name = 'AffiliationCodeInvalidError';
  }
}

export const userAffiliationTypeService = {
  async list(params: UserAffiliationTypeListParams = {}): Promise<UserAffiliationTypeListResponse> {
    try {
      const response = await apiClient.get<ResultEnvelope<UserAffiliationTypeListResponse>>(
        API_EDI_AFFILIATION_TYPES_LIST,
        {
          params: {
            includeInactive: params.includeInactive ?? undefined,
            classification: params.classification ?? undefined,
            isActive: params.isActive ?? undefined,
          },
        }
      );
      if (!response.data.succeeded) {
        throw new Error(response.data.messages?.join(', ') || 'Failed to list affiliation types');
      }
      return response.data.data ?? { items: [] };
    } catch (error) {
      logger.error('Failed to list affiliation types', error);
      throw error;
    }
  },

  async getById(id: number): Promise<UserAffiliationType | null> {
    const response = await apiClient.get<ResultEnvelope<GetByIdInner>>(API_EDI_AFFILIATION_TYPES_GET(id));
    if (!response.data.succeeded) {
      throw new Error(response.data.messages?.join(', ') || 'Failed to fetch affiliation type');
    }
    return response.data.data?.item ?? null;
  },

  async create(request: UserAffiliationTypeCreateRequest): Promise<{ affiliationTypeId: number }> {
    const response = await apiClient.post<ResultEnvelope<CreateInner>>(API_EDI_AFFILIATION_TYPES_CREATE, request);
    if (!response.data.succeeded) {
      if (response.data.statusCode === STATUS_AFFILIATION_CODE_INVALID_FORMAT) {
        throw new AffiliationCodeInvalidError(response.data.messages?.[0]);
      }
      throw new Error(response.data.messages?.join(', ') || 'Failed to create affiliation type');
    }
    return { affiliationTypeId: response.data.data?.affiliationTypeId ?? 0 };
  },

  async update(id: number, request: UserAffiliationTypeUpdateRequest): Promise<{ affiliationTypeId: number }> {
    const response = await apiClient.patch<ResultEnvelope<UpdateInner>>(
      API_EDI_AFFILIATION_TYPES_UPDATE(id),
      request
    );
    if (!response.data.succeeded) {
      throw new Error(response.data.messages?.join(', ') || 'Failed to update affiliation type');
    }
    return { affiliationTypeId: response.data.data?.affiliationTypeId ?? id };
  },

  async delete(id: number): Promise<{ affiliationTypeId: number; dependentUserCount: number }> {
    const response = await apiClient.delete<ResultEnvelope<DeleteInner>>(API_EDI_AFFILIATION_TYPES_DELETE(id));
    if (!response.data.succeeded) {
      if (response.data.statusCode === STATUS_AFFILIATION_REFERENCED) {
        throw new AffiliationTypeReferencedError(response.data.messages?.[0]);
      }
      throw new Error(response.data.messages?.join(', ') || 'Failed to delete affiliation type');
    }
    return {
      affiliationTypeId: response.data.data?.affiliationTypeId ?? id,
      dependentUserCount: response.data.data?.dependentUserCount ?? 0,
    };
  },
};
